<template>
    <Page>
        <Header />

        <div
            class="mt-10 flex w-full max-w-page flex-col items-center justify-center gap-4 rounded-lg bg-neutral-50 px-4 py-8 text-center shadow-md"
        >
            <div
                v-if="error?.statusCode && error?.statusCode !== 500"
                class="text-5xl font-semibold text-gray-700"
            >
                {{ error.statusCode }}
            </div>

            <!-- Custom message for "picks" and "picksV2" routes -->
            <h2 class="text-2xl font-medium text-gray-800">😕 {{ message }}</h2>
            <div class="mb-3 leading-6 text-neutral-1000/50">
                Need help?
                <NuxtLink
                    :href="`mailto:${config.public.contactEmail}`"
                    class="underline hover:text-neutral-900"
                    >{{ config.public.contactEmail }}</NuxtLink
                >
                <div
                    class="my-3 flex w-full flex-row items-center justify-center gap-2 px-5 py-2 font-600 text-sm leading-[24px] text-neutral-950"
                    @click="handleClick"
                >
                    <IconWhatsappFilled class="size-7 text-white" />
                    <span class="border-b-2 border-neutral-600"
                        >Message us</span
                    >
                </div>
            </div>
            <p class="text-lg text-neutral-500">
                It seems something went wrong. We apologize for the
                inconvenience.
            </p>

            <!-- Suggestion to get in touch or refresh the page -->
            <div class="mt-4 flex flex-col items-center gap-3">
                <button
                    class="rounded-md bg-gray-800 px-5 py-2 text-white shadow hover:bg-gray-700"
                    @click="handleError"
                >
                    Return Home
                </button>
                <button class="text-gray-600 underline" @click="refreshPage">
                    Refresh the page
                </button>
            </div>
        </div>
    </Page>
</template>

<script setup lang="ts">
import type { NuxtError } from 'nuxt/app'

import Page from '~/components/Page.vue'

const config = useRuntimeConfig()

const props = defineProps({
    error: Object as () => NuxtError,
})

// Check if this is a picks/picksV2 404 error and redirect to custom 404 page
const url = (props.error as any)?.url || ''
if (
    props.error?.statusCode === 404 &&
    (url.includes('/picks') || url.includes('/picksV2'))
) {
    await navigateTo('/404')
}

// Redirects to the home page on button click
const handleError = () => clearError({ redirect: '/' })

// Refreshes the page
const refreshPage = () => window.location.reload()

function handleClick() {
    window.open('https://wa.me/14257280395', '_blank')
}

// Customized message based on URL and error code
const message = computed(() => {
    if (props.error?.statusCode === 500) {
        console.log(props.error)
        if (url.includes('/picksV2')) {
            return `It seems like these deals expired. Get in touch with us to receive new ones.`
        }
        if (url.includes('/pick/')) {
            return `This deal has expired. Please reach out to get a fresh one.`
        }
        return `Something went wrong. Please try again soon.`
    }

    // General error messages for other statuses
    switch (props.error?.statusCode) {
        case 403:
        case 401:
            return `You don't have access to this page`
        case 404:
            return `Page not found`
        default:
            return `Whoops, something went wrong, we're working on fixing it!`
    }
})
</script>

<style scoped>
/* Styling for a softer, user-friendly look */
button {
    transition: background-color 0.2s;
}
</style>
